/**
 * Business-timezone-aware date helpers.
 *
 * The Cricket Academy operates on a single business timezone configured in
 * Business Settings (default: America/New_York). All "today", "this week",
 * and slot-boundary calculations MUST be anchored to that timezone — never
 * to the visitor's browser timezone — otherwise an admin who travels
 * across time zones (or a parent in California) sees the wrong day on
 * lane schedules and booking windows.
 *
 * This module wraps the existing `/api/admin/timezone-settings/public`
 * endpoint with a module-level cache and exposes synchronous helpers that
 * do timezone-aware date math without going through fragile JS Date
 * arithmetic on the browser's local clock.
 *
 *   • `useBusinessTimezone()` — React hook. Returns the current timezone
 *     string. Bootstraps the cache on first render. Default 'America/New_York'.
 *
 *   • `getTodayInTz(tz)` — Returns today's date as YYYY-MM-DD in the given
 *     IANA timezone. Uses Intl.DateTimeFormat for correctness around DST.
 *
 *   • `nowInTz(tz)` — Returns the current wall-clock time in the given
 *     timezone as a YYYY-MM-DDTHH:MM:SS string (no offset suffix). Useful
 *     for comparing against booking `date + start_time` strings.
 *
 *   • `addDaysToDateString(ymd, n)` — Pure string-based date math. DST-safe
 *     because it anchors at noon UTC and reads back the date in UTC.
 *
 *   • `mondayOfWeek(ymd)` — Returns the Monday of the ISO week containing
 *     the given YYYY-MM-DD date.
 *
 *   • `parseDateOnly(ymd)` — Builds a JS Date at local noon for the given
 *     YYYY-MM-DD. Safe to pass to Calendar pickers that don't care about
 *     the time component.
 *
 *   • `compareDateStrings(a, b)` — Lexicographic comparison of YYYY-MM-DD
 *     strings (negative if a<b, etc.) — works because ISO date strings
 *     sort correctly.
 */

import { useEffect, useState } from 'react';

const DEFAULT_TZ = 'America/New_York';

// Module-level cache so every component that calls useBusinessTimezone()
// after the first one gets the cached value synchronously.
let cachedTz = null;
let inflightFetch = null;

/**
 * Fetch the configured business timezone from the public endpoint. The
 * endpoint requires no auth and is safe to call from any page.
 */
const fetchBusinessTimezone = async () => {
  try {
    const apiUrl = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);
    if (!apiUrl) return DEFAULT_TZ;
    const res = await fetch(`${apiUrl}/api/admin/timezone-settings/public`);
    if (!res.ok) return DEFAULT_TZ;
    const data = await res.json();
    return data?.timezone || DEFAULT_TZ;
  } catch (_e) {
    return DEFAULT_TZ;
  }
};

/**
 * React hook — returns the business timezone string. Triggers a single
 * background fetch on first use; subsequent callers read from cache.
 */
export const useBusinessTimezone = () => {
  const [tz, setTz] = useState(cachedTz || DEFAULT_TZ);

  useEffect(() => {
    if (cachedTz) {
      if (tz !== cachedTz) setTz(cachedTz);
      return;
    }
    if (!inflightFetch) {
      inflightFetch = fetchBusinessTimezone().then((value) => {
        cachedTz = value;
        inflightFetch = null;
        return value;
      });
    }
    inflightFetch.then((value) => {
      if (value && value !== tz) setTz(value);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return tz;
};

/** Force-refresh the cached business timezone (call after admin updates it). */
export const refreshBusinessTimezone = async () => {
  cachedTz = null;
  inflightFetch = null;
  const value = await fetchBusinessTimezone();
  cachedTz = value;
  return value;
};

/**
 * Return today's date as YYYY-MM-DD in the given timezone.
 * Uses Intl.DateTimeFormat which handles DST transitions correctly.
 */
export const getTodayInTz = (tz = DEFAULT_TZ) => {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    const d = parts.find((p) => p.type === 'day')?.value;
    return `${y}-${m}-${d}`;
  } catch (_e) {
    // Fall back to browser local
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
};

/**
 * Return the current wall-clock time in the given timezone as a
 * YYYY-MM-DDTHH:MM:SS string. No offset suffix — meant for comparing
 * against booking `date + start_time` strings that are stored in the
 * business timezone.
 */
export const nowInTz = (tz = DEFAULT_TZ) => {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value;
    let hour = get('hour');
    // Intl returns "24" instead of "00" for midnight in some browsers.
    if (hour === '24') hour = '00';
    return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}:${get('second')}`;
  } catch (_e) {
    return new Date().toISOString().slice(0, 19);
  }
};

/**
 * Add `days` (may be negative) to a YYYY-MM-DD string and return the
 * resulting YYYY-MM-DD. Uses UTC math so it never drifts across DST.
 */
export const addDaysToDateString = (ymd, days) => {
  if (!ymd || typeof ymd !== 'string') return ymd;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const t = Date.UTC(y, m - 1, d) + days * 24 * 60 * 60 * 1000;
  const dt = new Date(t);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

/**
 * Difference in whole days between two YYYY-MM-DD strings (b - a).
 */
export const diffDays = (a, b) => {
  if (!a || !b) return 0;
  const [ya, ma, da] = a.split('-').map(Number);
  const [yb, mb, db] = b.split('-').map(Number);
  const t1 = Date.UTC(ya, ma - 1, da);
  const t2 = Date.UTC(yb, mb - 1, db);
  return Math.round((t2 - t1) / (24 * 60 * 60 * 1000));
};

/**
 * Return the Monday (start of ISO week) of the week containing `ymd`.
 */
export const mondayOfWeek = (ymd) => {
  if (!ymd) return ymd;
  const [y, m, d] = ymd.split('-').map(Number);
  // Use UTC so getUTCDay() doesn't shift due to local TZ.
  const t = Date.UTC(y, m - 1, d);
  const dt = new Date(t);
  const dow = dt.getUTCDay(); // 0=Sun, 1=Mon, ... 6=Sat
  // Distance back to Monday: Sun -> -6, Mon -> 0, Tue -> -1, ...
  const offset = dow === 0 ? -6 : 1 - dow;
  return addDaysToDateString(ymd, offset);
};

/**
 * Parse a YYYY-MM-DD into a JS Date at local noon. Safe for Calendar
 * pickers that ignore the time component. Avoids the classic
 * `new Date('2026-03-08')` UTC-midnight off-by-one trap.
 */
export const parseDateOnly = (ymd) => {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
};

/** Negative if a < b, 0 if equal, positive if a > b. Both must be YYYY-MM-DD. */
export const compareDateStrings = (a, b) => {
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

/**
 * Format a YYYY-MM-DD as a localized display string (e.g. "Mar 8, 2026")
 * without any timezone shifting.
 */
export const formatDateOnlyDisplay = (ymd, options = {}) => {
  if (!ymd) return '';
  const dt = parseDateOnly(ymd);
  if (!dt) return ymd;
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  });
};
